import React, { useCallback, useEffect, useState } from "react";
import {
  getAdminProfile,
  getWorkerProfileFields,
} from "../api/getAdminProfile";
import { updateProfile } from "../api/updateProfile";
import { Admin } from "../types/admin.type";
import { Worker } from "../types/worker.type";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import axios from "axios";
import { UPLOAD_URL } from "../config/config";

const UpdateProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  type User = Admin | Worker;
  const { user, isLoading } = useAuth();
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [workerData, setWorkerData] = useState<Worker | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!user) {
          setError("No user data found");
          return;
        }
        const data = await getAdminProfile(id);
        setUserData(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    if (!isLoading) {
      fetchProfile();
    }
  }, [id, isLoading, user]);

  useEffect(() => {
    const fetchWorker = async () => {
      try {
        if (userData?.type === "MANAGER" || userData?.type === "EMPLOYEE") {
          const data = await getWorkerProfileFields(id);
          setWorkerData(data);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    if (!isLoading) {
      fetchWorker();
    }
  }, [id, isLoading, userData]);

  const saveChanges = useCallback(async () => {
    if (!userData) return;
    try {
      setSaving(true);

      let profileImagePath = userData.profileImage;

      if (selectedFile && user) {
        const formData = new FormData();
        formData.append("profile_image", selectedFile); // 👈 match multer field name exactly

        let url = process.env.REACT_APP_BACKEND_UPLOAD?.replace(/\/+$/, "");
        url += `/api/users/${userData.id}`;
        if (!url) return;
        try {
          const response = await axios.put(url, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          // Access the returned data
          const uploadData = response.data;
          profileImagePath = uploadData.filePath;

          // You can now use `profileImagePath` to update state or send it elsewhere
        } catch (err) {
          console.error("Image upload failed:", err);
        }
      }

      // 2) Then update user profile with the new profileImage path + other data
      const data = {
        ...userData,
        profileImage: profileImagePath,
        ...(workerData ?? {}),
      };

      const updated = await updateProfile(userData.id, data, userData.type);

      alert("Profile updated successfully!");
      setUserData(updated);
      setWorkerData(updated);
      navigate("/profile");
    } catch (err) {
      console.error("Failed to update profile:", err);
    } finally {
      setSaving(false);
    }
  }, [navigate, userData, workerData, selectedFile, user]);

  if (loading) return <p>Loading profile...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!userData) return <p>No profile data found.</p>;

  const isWorker = (user: User): user is Worker => {
    return user.type === "EMPLOYEE" || user.type === "MANAGER";
  };

  const editableFields = isWorker(userData)
    ? (["name", "surname", "email", "city", "country"] as (keyof Worker)[])
    : (["name", "surname", "email", "city", "country"] as (keyof Admin)[]);

  // @ts-ignore
  return (
    <div
      style={{
        padding: "2rem",
        backgroundColor: "#eef2f5",
        minHeight: "100vh",
      }}
    >
      <div style={styles.container}>
        <h2>Update Your Profile</h2>

        <div style={styles.inputGroup}>
          <label htmlFor="profileImage" style={styles.label}>
            Profile Image
          </label>
          <img
            src={`${UPLOAD_URL}${userData.profileImage}`}
            alt="Profile"
            style={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              objectFit: "cover",
              marginBottom: "0.5rem",
            }}
          />
          <input
            type="file"
            id="profileImage"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setSelectedFile(e.target.files[0]);
              }
            }}
          />
        </div>

        {/* Fields from userData (common fields) */}
        {editableFields.map((field) => (
          <div key={field} style={styles.inputGroup}>
            <label htmlFor={field} style={styles.label}>
              {field}
            </label>
            <input
              type={
                typeof (userData as any)[field] === "number" ? "number" : "text"
              }
              id={field}
              name={field}
              placeholder={`Your ${field}`}
              style={styles.input}
              value={(userData as any)[field] ?? ""}
              onChange={(e) =>
                setUserData((prev) => {
                  if (!prev) return prev;

                  const updatedValue =
                    typeof (prev as any)[field] === "number"
                      ? Number(e.target.value)
                      : e.target.value;

                  return {
                    ...prev,
                    [field]: updatedValue,
                  };
                })
              }
            />
          </div>
        ))}

        {/* Worker-specific fields from workerData */}
        {workerData && (
          <>
            <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>
              Salary & Hour Details
            </h3>
            {[
              "normalHour",
              "normalSalaryPerHour",
              "holidayHours",
              "holidaySalaryPerHour",
              "extraWorkHours",
              "extraWorkSalaryPerHour",
              "sundayHours",
              "sundaySalaryPerHour",
              "nightShiftHours",
              "nightShiftSalaryPerHour",
            ].map((field) => (
              <div key={field} style={styles.inputGroup}>
                <label htmlFor={field} style={styles.label}>
                  {field}
                </label>
                <input
                  type={
                    typeof (workerData as any)[field] === "number"
                      ? "number"
                      : "text"
                  }
                  id={field}
                  name={field}
                  placeholder={`Your ${field}`}
                  style={styles.input}
                  value={(workerData as any)[field] ?? ""}
                  onChange={(e) =>
                    setWorkerData((prev) => {
                      if (!prev) return prev;

                      const updatedValue =
                        typeof (prev as any)[field] === "number"
                          ? Number(e.target.value)
                          : e.target.value;

                      return {
                        ...prev,
                        [field]: updatedValue,
                      };
                    })
                  }
                />
              </div>
            ))}
          </>
        )}

        <button
          onClick={saveChanges}
          disabled={saving}
          style={{
            ...styles.button,
            backgroundColor: saving ? "#ccc" : "#007bff",
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};
const styles = {
  container: {
    maxWidth: "600px",
    margin: "0 auto",
    padding: "2rem",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  title: {
    textAlign: "center",
    fontSize: "1.8rem",
    marginBottom: "1.5rem",
    color: "#333",
  },
  inputGroup: {
    marginBottom: "1.25rem",
  },
  label: {
    display: "block",
    marginBottom: "0.5rem",
    fontWeight: "600",
    color: "#555",
  },
  input: {
    width: "100%",
    padding: "0.75rem",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "1rem",
    backgroundColor: "#f7f7f7",
  },
  button: {
    width: "100%",
    padding: "0.85rem",
    fontSize: "1rem",
    fontWeight: "600",
    color: "white",
    border: "none",
    borderRadius: "8px",
    transition: "background-color 0.3s ease",
  },
};

export default UpdateProfile;
